/**
 * React Query hooks for the Comms tool.
 *
 *   useCommsSummary(date)   GET {API_BASE}/v1/comms/summary?date=YYYY-MM-DD
 *   useCommsCallbacks(date) GET {API_BASE}/v1/comms/callbacks?date=YYYY-MM-DD
 *   useCommsVoicemail()     GET {API_BASE}/v1/comms/voicemail
 *
 * Backend is the api service (dev.api.ampersandbrands.com). All three
 * endpoints require the comms:read capability (server-side enforced).
 * The page calls all three independently so each section can render
 * its own loading / error / empty state without blocking the others.
 */

import { useQuery } from '@tanstack/react-query';
import { apiGet } from './api';
import { API_BASE } from './apiBase';

const COMMS_BASE = `${API_BASE}/v1/comms`;

// ---- Summary ----------------------------------------------------------------

export type CommsSummaryHour = {
  hour: number;
  count: number;
};

export type CommsSummaryHourDest = {
  hour: string;
  reached: string;
  count: number;
};

export type CommsSummary = {
  date: string;
  total: number;
  /** calls the phone system could handle: total minus after_hours */
  served: number;
  live: number;
  voicemail: number;
  /** reached a voicemail box but left no message */
  no_message: number;
  /** reached the closed greeting; excluded from the abandoned rate */
  after_hours: number;
  abandoned: number;
  /** 0..1 fraction of served calls that were abandoned */
  abandoned_rate: number;
  by_hour: CommsSummaryHour[];
  /** hourly breakdown by destination the caller was trying to reach */
  by_hour_dest: CommsSummaryHourDest[];
};

export type UseCommsSummaryResult = {
  summary: CommsSummary | null;
  isLoading: boolean;
  error: Error | null;
};

export function useCommsSummary(date: string): UseCommsSummaryResult {
  const query = useQuery<CommsSummary, Error>({
    queryKey: ['comms-summary', date],
    queryFn: () => apiGet<CommsSummary>(`${COMMS_BASE}/summary?date=${encodeURIComponent(date)}`),
    staleTime: 60_000,
    retry: 1,
  });
  return {
    summary: query.data ?? null,
    isLoading: query.isLoading,
    error: query.error,
  };
}

// ---- Callbacks --------------------------------------------------------------

export type CommsCallback = {
  time: string;
  caller: string;
  /** caller's name from carrier CNAM, null when unknown */
  caller_name: string | null;
  did_label: string;
  /** friendly name of where they gave up (menu, extension, ring group) */
  reached: string | null;
  gave_up_at: string;
  /** total time in the system before giving up, seconds */
  wait_seconds: number;
};

export type CommsCallbacksResponse = {
  callbacks: CommsCallback[];
};

export type UseCommsCallbacksResult = {
  callbacks: CommsCallback[];
  isLoading: boolean;
  error: Error | null;
};

export function useCommsCallbacks(date: string): UseCommsCallbacksResult {
  const query = useQuery<CommsCallbacksResponse, Error>({
    queryKey: ['comms-callbacks', date],
    queryFn: () =>
      apiGet<CommsCallbacksResponse>(`${COMMS_BASE}/callbacks?date=${encodeURIComponent(date)}`),
    staleTime: 60_000,
    retry: 1,
  });
  return {
    callbacks: query.data?.callbacks ?? [],
    isLoading: query.isLoading,
    error: query.error,
  };
}

// ---- Calls (full list) ------------------------------------------------------

export type CommsCall = {
  linkedid: string;
  /** call time, 12-hour (e.g. "9:51 AM") */
  time: string;
  /** call date, MM/DD (the log spans a range) */
  date: string;
  /** the "from" party: external number (in) or extension (out/internal) */
  caller: string | null;
  /** caller's name: carrier CNAM (in) or the extension owner (out/internal) */
  caller_name: string | null;
  /** the from-party's extension/number, for hover */
  caller_ext: string | null;
  /** the "to" party, by name: extension, ring group, menu, VM box, or number */
  reached: string | null;
  /** the to-party's extension, for hover */
  reached_ext: string | null;
  /** in | out | internal */
  direction: string;
  outcome: string;
  gave_up_at: string | null;
  /** answered/talk time, seconds (0 unless answered) */
  talk_seconds: number;
  /** total time in the system, seconds (wait before giving up) */
  wait_seconds: number;
  /** true when a voicemail recording is available to play */
  has_recording: boolean;
};

export type CommsCallsResponse = {
  calls: CommsCall[];
};

export type CommsCallsFilters = {
  /** inclusive date range (YYYY-MM-DD) */
  date_from: string;
  date_to: string;
  /** one outcome value, or '' for all */
  outcome?: string;
  /** in | out | internal | all ('' or undefined = incoming only) */
  direction?: string;
  /** free-text match on caller number, name, or reached name */
  q?: string;
};

export type UseCommsCallsResult = {
  calls: CommsCall[];
  isLoading: boolean;
  error: Error | null;
};

/** Build the query string shared by the calls list and the xlsx export. */
function commsCallsQuery(f: CommsCallsFilters): string {
  const p = new URLSearchParams({ date_from: f.date_from, date_to: f.date_to });
  if (f.outcome) p.set('outcome', f.outcome);
  if (f.direction) p.set('direction', f.direction);
  if (f.q && f.q.trim()) p.set('q', f.q.trim());
  return p.toString();
}

export function useCommsCalls(filters: CommsCallsFilters): UseCommsCallsResult {
  const qs = commsCallsQuery(filters);
  const query = useQuery<CommsCallsResponse, Error>({
    queryKey: ['comms-calls', qs],
    queryFn: () => apiGet<CommsCallsResponse>(`${COMMS_BASE}/calls?${qs}`),
    staleTime: 60_000,
    retry: 1,
  });
  return {
    calls: query.data?.calls ?? [],
    isLoading: query.isLoading,
    error: query.error,
  };
}

/** Fetch a short-lived presigned URL for a call's voicemail recording (mp3). */
export async function fetchCommsRecordingUrl(linkedid: string): Promise<string> {
  const r = await apiGet<{ url: string }>(
    `${COMMS_BASE}/calls/${encodeURIComponent(linkedid)}/recording`,
  );
  return r.url;
}

/** Download the Call Log as an Excel workbook with the current filters. */
export async function downloadCommsCallsXlsx(filters: CommsCallsFilters): Promise<void> {
  const res = await fetch(`${COMMS_BASE}/calls/export?${commsCallsQuery(filters)}`, {
    credentials: 'include',
  });
  if (!res.ok) throw new Error(`Export failed (${res.status})`);
  const blob = await res.blob();
  const cd = res.headers.get('content-disposition') ?? '';
  const m = /filename="?([^"]+)"?/.exec(cd);
  const filename = m?.[1] ?? `comms-call-log-${filters.date_from}_to_${filters.date_to}.xlsx`;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

// ---- Voicemail --------------------------------------------------------------

export type CommsVoicemailBox = {
  box: string;
  label: string;
  dest_email: string | null;
  new_today: number;
  unheard_total: number;
  last_message_at: string | null;
  email_ok: boolean;
  /** voicemail emails delivered to the box in the last 7 days */
  email_received_7d: number;
  /** of those, how many were opened in Outlook */
  email_opened_7d: number;
};

export type CommsVoicemailResponse = {
  boxes: CommsVoicemailBox[];
};

export type UseCommsVoicemailResult = {
  boxes: CommsVoicemailBox[];
  isLoading: boolean;
  error: Error | null;
};

export function useCommsVoicemail(): UseCommsVoicemailResult {
  const query = useQuery<CommsVoicemailResponse, Error>({
    queryKey: ['comms-voicemail'],
    queryFn: () => apiGet<CommsVoicemailResponse>(`${COMMS_BASE}/voicemail`),
    staleTime: 120_000,
    retry: 1,
  });
  return {
    boxes: query.data?.boxes ?? [],
    isLoading: query.isLoading,
    error: query.error,
  };
}

// ---- Helpers ----------------------------------------------------------------

/** Format a raw phone number string as (NNN) NNN-NNNN. Passes through
 *  anything that does not look like a 10-digit US number. */
export function formatPhone(raw: string): string {
  const digits = raw.replace(/\D/g, '');
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  if (digits.length === 11 && digits[0] === '1') {
    return `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  }
  return raw;
}

/** Return today's date as a YYYY-MM-DD string (local time). */
export function todayDate(): string {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

/** Friendly label for the gave_up_at stage value. */
export function gaveUpLabel(stage: string): string {
  switch (stage.toLowerCase()) {
    case 'greeting': return 'greeting';
    case 'menu': return 'menu';
    case 'ringing': return 'ringing';
    case 'voicemail-no-msg':
    case 'voicemail_no_msg': return 'voicemail (no msg)';
    default: return stage;
  }
}

/** Friendly label for a call outcome value. */
export function outcomeLabel(outcome: string): string {
  switch (outcome) {
    case 'live': return 'Answered';
    case 'voicemail': return 'Voicemail';
    case 'no_message': return 'No message';
    case 'after_hours': return 'After hours';
    case 'abandoned': return 'Abandoned';
    default: return outcome;
  }
}

/** Tailwind Pill tone classes for a call outcome. */
export function outcomeTone(outcome: string): string {
  switch (outcome) {
    case 'live':
      return 'bg-green-100 text-green-900 dark:bg-green-900/40 dark:text-green-200';
    case 'voicemail':
      return 'bg-blue-100 text-blue-900 dark:bg-blue-900/40 dark:text-blue-200';
    case 'abandoned':
      return 'bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-200';
    case 'no_message':
    case 'after_hours':
    default:
      return 'bg-amp-line/60 text-amp-ink dark:bg-amp-dark-line/60 dark:text-amp-dark-ink';
  }
}

/** Format a talk duration in seconds as "m:ss"; blank for zero/unanswered. */
export function formatDuration(seconds: number): string {
  if (!seconds || seconds <= 0) return '';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}
